import {
	FILES_MAX_LENGTH,
	FILE_MAX_SIZE,
} from "components/ProjectSpecificComponents/Constants/GLOBAL_CONSTANTS"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import { convertToReadableBytes } from "components/ProjectSpecificComponents/Functions/convertToReadableBytes"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { TOnError, TOnFile, TOnUpdateFiles } from "./Interfaces"

interface Props {
	accept?: string[]
	children?: React.ReactNode
	/**
	 * Disables the abililty to add multiple files,
	 * and each new file added overwrites the last.
	 */
	disableMultipleFiles?: boolean
	disabled?: boolean
	/**
	 * Allows to control internal files state externally
	 */
	files?: File[]
	/**
	 * Fires whenever a single file is successfully added to the list of files
	 */
	onFile?: TOnFile
	/**
	 * Fires every time the full list of files is updated
	 */
	onUpdateFiles?: TOnUpdateFiles
	onError?: TOnError
	containerStyle?: React.CSSProperties
}

const UploadButton = ({
	accept,
	children,
	disableMultipleFiles = false,
	disabled = false,
	files,
	onFile = () => {},
	onUpdateFiles = () => {},
	onError = () => {},
	containerStyle,
}: Props) => {
	const store = useStore()
	const { t } = useTranslation()
	const acceptedFileExts = accept
	const inputRef = useRef<HTMLInputElement>(null)
	const [currentFiles, setCurrentFiles] = useState<File[]>([])

	useEffect(() => {
		if (!files) setCurrentFiles([])
		else setCurrentFiles(files)
	}, [files])

	const _processFiles = async (fileList: FileList) => {
		let fileNamesDenied: string[] = []
		let errorSize: string[] = []
		let errorCount: string[] = []
		let filteredFiles: File[] = []
		let fullSize = currentFiles.reduce((accumulator, currentValue: File) => {
			return accumulator + currentValue.size
		}, 0)
		let listLength = currentFiles.length

		let files = Array.from(fileList).map((file) => {
			let { name, size } = file
			let fileExt = name.substring(name.lastIndexOf(".")).toLowerCase()

			fullSize = fullSize + size

			++listLength

			if (size == 0 || size > FILE_MAX_SIZE || fullSize > FILE_MAX_SIZE) {
				errorSize.push(name)
				return
			}

			if (listLength > FILES_MAX_LENGTH) {
				errorCount.push(name)
				return
			}

			let reader = new FileReader()

			return new Promise((resolve) => {
				// Resolve the promise after reading file
				reader.onload = function () {
					if (acceptedFileExts && !acceptedFileExts.includes(fileExt)) {
						fileNamesDenied.push(name)
					} else {
						filteredFiles.push(file)
						onFile(file)
					}
					resolve(reader.result)
				}
				reader.readAsArrayBuffer(file)
			})
		})

		// At this point you'll have an array of results
		return await Promise.all(files).then(() => {
			return { filteredFiles, fileNamesDenied, errorSize, errorCount }
		})
	}

	const _onFiles = async (files: FileList) => {
		let { filteredFiles, fileNamesDenied, errorSize, errorCount } = await _processFiles(files)
		if (filteredFiles.length) {
			let newFiles
			if (disableMultipleFiles) newFiles = filteredFiles
			else newFiles = [...filteredFiles, ...currentFiles]

			setCurrentFiles(newFiles)
			onUpdateFiles(newFiles)
		}

		if (fileNamesDenied.length) {
			onError({
				acceptedFileExtsArr: acceptedFileExts ?? null,
				acceptedFileExts: acceptedFileExts?.join(", ") ?? null,
				invalidFilesNames: fileNamesDenied,
			})
		}

		if (errorCount.length) {
			let fileNames = `${errorCount.map((value) => {
				return " " + value
			})}`
			store.setInfoModalConfig({
				titleKey: `${t("file.fail.onerror-count")} ${FILES_MAX_LENGTH}`,
				descriptionKey: fileNames,
				type: "warning",
			})
		}

		if (errorSize.length) {
			let fileNames = `${errorSize.map((value) => {
				return " " + value
			})}`
			let size = convertToReadableBytes(FILE_MAX_SIZE)
			store.setInfoModalConfig({
				titleKey: `${t("file.fail.onerror-size")} ${size}`,
				descriptionKey: fileNames,
				type: "warning",
			})
		}
	}

	const onDropFile = async (e: React.DragEvent) => {
		e.preventDefault()
		e.stopPropagation()
		if (currentFiles.length >= FILES_MAX_LENGTH) {
			store.setInfoModalConfig({
				titleKey: `${t("file.fail.limit")} ${FILES_MAX_LENGTH}`,
				type: "warning",
			})
			return
		}
		const files = e.dataTransfer.files
		if (!files) return

		if (Array.from(files).length > FILES_MAX_LENGTH) {
			store.setInfoModalConfig({
				titleKey: `${t("file.fail.limit")} ${FILES_MAX_LENGTH}`,
				type: "warning",
			})
			return
		}
		_onFiles(files)
	}

	const onChangeInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault()
		if (currentFiles.length >= FILES_MAX_LENGTH) {
			store.setInfoModalConfig({
				titleKey: `${t("file.fail.limit")} ${FILES_MAX_LENGTH}`,
				type: "warning",
			})
			e.target.value = ""
			return
		}

		const files = e.target.files
		if (!files) return

		if (Array.from(files).length > FILES_MAX_LENGTH) {
			store.setInfoModalConfig({
				titleKey: `${t("file.fail.limit")} ${FILES_MAX_LENGTH}`,
				type: "warning",
			})
			e.target.value = ""
			return
		}
		await _onFiles(files)
		e.target.value = ""
	}

	return (
		<>
			<div
				className={disabled ? "cursor-blocked" : "pointer"}
				style={containerStyle}
				onDrop={disabled ? undefined : onDropFile}
				onDragOver={(e) => e.preventDefault()}
				onClick={(e) => {
					e.stopPropagation()
					if (disabled) return

					inputRef.current?.click()
				}}
			>
				{children ? (
					children
				) : (
					// Default UI (purely visual)
					<input
						style={{
							width: "100%",
							height: "100%",
							cursor: disabled ? "" : "pointer",
						}}
						value="Upload File"
						type="button"
						disabled={disabled}
					/>
				)}
			</div>
			<input
				style={{ display: "none" }}
				type="file"
				multiple={!disableMultipleFiles}
				accept={acceptedFileExts?.join(",") || undefined}
				onClick={(e) => e.stopPropagation()}
				onChange={onChangeInput}
				ref={inputRef}
			/>
		</>
	)
}

export default UploadButton
